import { createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";
import type {
  DrawingState, DrawingToolInstance, DrawingToolName, DrawingCoordinates,
  MainLineSettings, BackgroundSettings, FibonacciSettings, ChartPriceRange,
} from "./types";
import { DEFAULT_MAIN_LINE, DEFAULT_BACKGROUND, DEFAULT_FIBONACCI } from "./defaultSettings";

const initialState: DrawingState = {
  instances: [],
  chartPriceRange: null,
  verticalLineInitialTimestamp: null,
  visibleCandleCount: null,
  timeframeMs: null,
};

const drawingSlice = createSlice({
  name: "drawing",
  initialState,
  reducers: {
    selectTool: (state, { payload }: PayloadAction<DrawingToolName>) => {
      state.instances.push({
        id: nanoid(), name: payload, coordinates: null,
        mainLine: DEFAULT_MAIN_LINE, background: DEFAULT_BACKGROUND, fibonacci: DEFAULT_FIBONACCI,
      });
    },
    setDrawingInstances: (state, { payload }: PayloadAction<DrawingToolInstance[]>) => {
      state.instances = payload;
    },
    updateInstanceCoordinates: (state, { payload }: PayloadAction<{ id: string; coordinates: DrawingCoordinates }>) => {
      const inst = state.instances.find((i) => i.id === payload.id);
      if (inst) inst.coordinates = payload.coordinates;
    },
    removeTool: (state, { payload }: PayloadAction<string>) => {
      state.instances = state.instances.filter((i) => i.id !== payload);
    },
    updateToolMainLine: (state, { payload }: PayloadAction<{ id: string; mainLine: MainLineSettings }>) => {
      const inst = state.instances.find((i) => i.id === payload.id);
      if (inst) inst.mainLine = payload.mainLine;
    },
    updateToolBackground: (state, { payload }: PayloadAction<{ id: string; background: BackgroundSettings }>) => {
      const inst = state.instances.find((i) => i.id === payload.id);
      if (inst) inst.background = payload.background;
    },
    updateToolFibonacci: (state, { payload }: PayloadAction<{ id: string; fibonacci: FibonacciSettings }>) => {
      const inst = state.instances.find((i) => i.id === payload.id);
      if (inst) inst.fibonacci = payload.fibonacci;
    },
    updateChartPriceRange: (state, { payload }: PayloadAction<ChartPriceRange>) => {
      state.chartPriceRange = payload;
    },
    updateVerticalLineInitialTimestamp: (state, { payload }: PayloadAction<number>) => {
      state.verticalLineInitialTimestamp = payload;
    },
    updateVisibleCandleCount: (state, { payload }: PayloadAction<number>) => {
      state.visibleCandleCount = payload;
    },
    updateTimeframeMs: (state, { payload }: PayloadAction<number>) => {
      state.timeframeMs = payload;
    },
    resetTools: (state) => { state.instances = []; },
  },
});

export const {
  selectTool,
  setDrawingInstances,
  updateInstanceCoordinates,
  removeTool,
  updateToolMainLine,
  updateToolBackground,
  updateToolFibonacci,
  updateChartPriceRange,
  updateVerticalLineInitialTimestamp,
  updateVisibleCandleCount,
  updateTimeframeMs,
  resetTools,
} = drawingSlice.actions;

export default drawingSlice.reducer;
